import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Building2, ArrowRight, ArrowLeft, CheckCircle2, Upload, Eye, EyeOff, ShieldCheck, FileText, CreditCard, MapPin, Calendar } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { toast } from 'sonner';
import { BillvastLogo } from '../components/ui/BillvastLogo';
export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const {
    login,
    dispatch
  } = useAppContext();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // Step 1: Account Type
  const [accountType, setAccountType] = useState<'individual' | 'organization'>('individual');
  // Step 2: Basic Info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [cacNumber, setCacNumber] = useState('');
  const [ageError, setAgeError] = useState('');
  // Step 3: KYC
  // Individual
  const [idType, setIdType] = useState('nin');
  const [idNumber, setIdNumber] = useState('');
  const [idUploaded, setIdUploaded] = useState(false);
  const [bvn, setBvn] = useState('');
  const [bankName, setBankName] = useState('gtbank');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankProofUploaded, setBankProofUploaded] = useState(false);
  const [addressType, setAddressType] = useState('utility_bill');
  const [addressUploaded, setAddressUploaded] = useState(false);
  // Organization
  const [cacUploaded, setCacUploaded] = useState(false);
  const [officeAddress, setOfficeAddress] = useState('');
  // Step 4: Review
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const validateAge = (dob: string) => {
    if (!dob) return false;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || m === 0 && today.getDate() < birthDate.getDate()) {
      age--;
    }
    if (age < 18) {
      setAgeError('You must be at least 18 years old to register.');
      return false;
    }
    setAgeError('');
    return true;
  };
  const handleNext = () => {
    if (step === 2) {
      if (!name || !email || !phone || !password || !confirmPassword || !dateOfBirth) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (!validateAge(dateOfBirth)) {
        return;
      }
      if (accountType === 'organization' && (!organizationName || !cacNumber)) {
        toast.error('Please provide organization details');
        return;
      }
    }
    if (step === 3) {
      if (accountType === 'individual') {
        if (!idNumber || !idUploaded || !bvn || !accountNumber || !bankProofUploaded || !addressUploaded) {
          toast.error('Please complete all KYC requirements');
          return;
        }
        if (bvn.length !== 11) {
          toast.error('BVN must be 11 digits');
          return;
        }
        if (accountNumber.length !== 10) {
          toast.error('Account number must be 10 digits');
          return;
        }
      } else {
        if (!cacUploaded || !bvn || !accountNumber || !bankProofUploaded || !officeAddress) {
          toast.error('Please complete all KYC requirements');
          return;
        }
        if (bvn.length !== 11) {
          toast.error('BVN must be 11 digits');
          return;
        }
        if (accountNumber.length !== 10) {
          toast.error('Account number must be 10 digits');
          return;
        }
      }
    }
    setDirection(1);
    setStep(prev => Math.min(prev + 1, 4));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('You must agree to the Terms of Service');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      // Update user in context
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          name,
          email,
          phone,
          dateOfBirth,
          accountType,
          ...(accountType === 'organization' ? {
            organizationName,
            cacNumber,
            officeAddress
          } : {})
        }
      });
      login();
      toast.success('Account created successfully! Welcome to Billvast');
      navigate('/dashboard');
    }, 2000);
  };
  const simulateUpload = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    toast.success('Document uploaded successfully');
    setter(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1897">
      <div className="sm:mx-auto sm:w-full sm:max-w-xl" data-id="element-1898">
        <div className="flex justify-center mb-6" data-id="element-1899">
          <BillvastLogo size="xl" data-id="element-1900" />
        </div>

        {/* Step Indicator */}
        <div className="mb-8 px-4" data-id="element-1901">
          <div className="flex items-center justify-between relative" data-id="element-1902">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 -z-10 rounded-full" data-id="element-1903"></div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand-primary -z-10 rounded-full transition-all duration-500" style={{
            width: `${(step - 1) / 3 * 100}%`
          }} data-id="element-1904"></div>

            {[1, 2, 3, 4].map(s => <div key={s} className="flex flex-col items-center gap-2 bg-gray-50 px-2" data-id="element-1905">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-300 ${step > s ? 'bg-brand-primary text-white' : step === s ? 'bg-white border-2 border-brand-primary text-brand-primary ring-4 ring-brand-primary/20' : 'bg-gray-200 text-gray-500'}`} data-id="element-1906">
                  {step > s ? <CheckCircle2 size={16} data-id="element-1907" /> : s}
                </div>
              </div>)}
          </div>
          <div className="flex justify-between mt-2 px-1" data-id="element-1908">
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1909">
              Type
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1910">
              Info
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1911">
              KYC
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1912">
              Review
            </span>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100 overflow-hidden relative min-h-[400px]" data-id="element-1913">
          <AnimatePresence mode="wait" custom={direction} data-id="element-1914">
            <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="w-full" data-id="element-1915">
              {/* STEP 1: Account Type */}
              {step === 1 && <div className="space-y-6" data-id="element-1916">
                  <div className="text-center mb-8" data-id="element-1917">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1918">
                      Choose Account Type
                    </h2>
                    <p className="text-sm text-brand-text-light mt-2" data-id="element-1919">
                      Select how you want to use Billvast
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1920">
                    <button onClick={() => setAccountType('individual')} className={`p-6 rounded-2xl border-2 text-left transition-all ${accountType === 'individual' ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-gray-200 hover:border-brand-primary/50 hover:bg-gray-50'}`} data-id="element-1921">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${accountType === 'individual' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1922">
                        <User size={24} data-id="element-1923" />
                      </div>
                      <h3 className="text-lg font-bold text-brand-text-dark mb-2" data-id="element-1924">
                        Personal Account
                      </h3>
                      <p className="text-sm text-brand-text-light leading-relaxed" data-id="element-1925">
                        For individuals who want to donate, fundraise, or manage
                        personal finances
                      </p>
                    </button>

                    <button onClick={() => setAccountType('organization')} className={`p-6 rounded-2xl border-2 text-left transition-all ${accountType === 'organization' ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-gray-200 hover:border-brand-primary/50 hover:bg-gray-50'}`} data-id="element-1926">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${accountType === 'organization' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1927">
                        <Building2 size={24} data-id="element-1928" />
                      </div>
                      <h3 className="text-lg font-bold text-brand-text-dark mb-2" data-id="element-1929">
                        Organization Account
                      </h3>
                      <p className="text-sm text-brand-text-light leading-relaxed" data-id="element-1930">
                        For NGOs, businesses, and registered organizations
                      </p>
                    </button>
                  </div>
                </div>}

              {/* STEP 2: Basic Info */}
              {step === 2 && <div className="space-y-5" data-id="element-1931">
                  <div className="text-center mb-6" data-id="element-1932">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1933">
                      Basic Information
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1934">
                      Tell us about yourself
                    </p>
                  </div>

                  {accountType === 'organization' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1935">
                      <div data-id="element-1936">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1937">
                          Organization Name *
                        </label>
                        <input type="text" value={organizationName} onChange={e => setOrganizationName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="e.g. Hope Foundation" data-id="element-1938" />
                      </div>
                      <div data-id="element-1939">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1940">
                          CAC Number *
                        </label>
                        <input type="text" value={cacNumber} onChange={e => setCacNumber(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="e.g. RC123456" data-id="element-1941" />
                      </div>
                    </div>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1942">
                    <div data-id="element-1943">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1944">
                        Full Name *
                      </label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="Adaeze Obi" data-id="element-1945" />
                    </div>
                    <div data-id="element-1946">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1947">
                        Date of Birth *
                      </label>
                      <input type="date" value={dateOfBirth} onChange={e => {
                    setDateOfBirth(e.target.value);
                    validateAge(e.target.value);
                  }} className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none ${ageError ? 'border-red-300 focus:ring-red-200 focus:border-red-500' : 'border-gray-300 focus:ring-brand-primary focus:border-brand-primary'}`} data-id="element-1948" />
                      {ageError && <p className="text-xs text-red-500 mt-1" data-id="element-1949">{ageError}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1950">
                    <div data-id="element-1951">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1952">
                        Email Address *
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="john@example.com" data-id="element-1953" />
                    </div>
                    <div data-id="element-1954">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1955">
                        Phone Number *
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="+234 800 000 0000" data-id="element-1956" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1957">
                    <div data-id="element-1958">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1959">
                        Password *
                      </label>
                      <div className="relative" data-id="element-1960">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none pr-10" placeholder="Min. 8 characters" data-id="element-1961" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1962">
                          {showPassword ? <EyeOff size={18} data-id="element-1963" /> : <Eye size={18} data-id="element-1964" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1965">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1966">
                        Confirm Password *
                      </label>
                      <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="Confirm password" data-id="element-1967" />
                    </div>
                  </div>
                </div>}

              {/* STEP 3: KYC */}
              {step === 3 && <div className="space-y-5" data-id="element-1968">
                  <div className="text-center mb-6" data-id="element-1969">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1970">
                      Identity Verification
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1971">
                      Please provide your KYC documents. All applicants must be
                      18 years or older.
                    </p>
                  </div>

                  {accountType === 'individual' ? <>
                      {/* Individual KYC */}
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-2" data-id="element-1972">
                        <p className="text-xs text-blue-700 font-medium" data-id="element-1973">
                          Required: Unexpired Government-Issued Photo ID, BVN,
                          Proof of Bank Ownership, and Proof of Address
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1974">
                        <div data-id="element-1975">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1976">
                            Unexpired Photo ID Type
                          </label>
                          <select value={idType} onChange={e => setIdType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-1977">
                            <option value="nin" data-id="element-1978">National ID (NIN Slip)</option>
                            <option value="voters_card" data-id="element-1979">Voter's Card</option>
                            <option value="drivers_license" data-id="element-1980">
                              Driver's License
                            </option>
                            <option value="passport" data-id="element-1981">
                              International Passport
                            </option>
                          </select>
                        </div>
                        <div data-id="element-1982">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1983">
                            ID Number
                          </label>
                          <input type="text" value={idNumber} onChange={e => setIdNumber(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none uppercase" placeholder="Enter ID Number" data-id="element-1984" />
                        </div>
                      </div>

                      <div data-id="element-1985">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1986">
                          Upload Unexpired Government-Issued Photo ID
                        </label>
                        <button type="button" onClick={() => simulateUpload(setIdUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${idUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-1987">
                          {idUploaded ? <CheckCircle2 size={24} data-id="element-1988" /> : <Upload size={24} data-id="element-1989" />}
                          <span className="font-medium text-sm" data-id="element-1990">
                            {idUploaded ? 'Photo ID Uploaded' : 'Upload Photo ID (must be unexpired)'}
                          </span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1991">
                        <div data-id="element-1992">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1993">
                            Bank Verification Number (BVN)
                          </label>
                          <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="11-digit BVN" data-id="element-1994" />
                        </div>
                        <div data-id="element-1995">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1996">
                            Bank Name
                          </label>
                          <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-1997">
                            <option value="gtbank" data-id="element-1998">GTBank</option>
                            <option value="access" data-id="element-1999">Access Bank</option>
                            <option value="firstbank" data-id="element-2000">First Bank</option>
                            <option value="uba" data-id="element-2001">UBA</option>
                            <option value="zenith" data-id="element-2002">Zenith Bank</option>
                            <option value="stanbic" data-id="element-2003">Stanbic IBTC</option>
                            <option value="fcmb" data-id="element-2004">FCMB</option>
                            <option value="fidelity" data-id="element-2005">Fidelity Bank</option>
                            <option value="sterling" data-id="element-2006">Sterling Bank</option>
                            <option value="wema" data-id="element-2007">Wema Bank</option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-2008">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2009">
                          Account Number
                        </label>
                        <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="10-digit Account Number" data-id="element-2010" />
                      </div>

                      <div data-id="element-2011">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2012">
                          Proof of Bank Ownership (Bank Statement or Letter)
                        </label>
                        <button type="button" onClick={() => simulateUpload(setBankProofUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${bankProofUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-2013">
                          {bankProofUploaded ? <CheckCircle2 size={24} data-id="element-2014" /> : <CreditCard size={24} data-id="element-2015" />}
                          <span className="font-medium text-sm" data-id="element-2016">
                            {bankProofUploaded ? 'Bank Ownership Proof Uploaded' : 'Upload Bank Statement or Reference Letter'}
                          </span>
                        </button>
                      </div>

                      <div data-id="element-2017">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2018">
                          Proof of Address Type
                        </label>
                        <select value={addressType} onChange={e => setAddressType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-2019">
                          <option value="utility_bill" data-id="element-2020">
                            Utility Bill (last 3 months)
                          </option>
                          <option value="bank_statement" data-id="element-2021">
                            Bank Statement (last 3 months)
                          </option>
                          <option value="tenancy_agreement" data-id="element-2022">
                            Tenancy Agreement
                          </option>
                        </select>
                      </div>

                      <div data-id="element-2023">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2024">
                          Upload Proof of Address
                        </label>
                        <button type="button" onClick={() => simulateUpload(setAddressUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${addressUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-2025">
                          {addressUploaded ? <CheckCircle2 size={24} data-id="element-2026" /> : <Upload size={24} data-id="element-2027" />}
                          <span className="font-medium text-sm" data-id="element-2028">
                            {addressUploaded ? 'Address Document Uploaded' : 'Upload Proof of Address'}
                          </span>
                        </button>
                      </div>
                    </> : <>
                      {/* Organization KYC */}
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-2" data-id="element-2029">
                        <p className="text-xs text-blue-700 font-medium" data-id="element-2030">
                          Required: CAC Certificate, BVN, Proof of Bank
                          Ownership, and Office Address
                        </p>
                      </div>

                      <div data-id="element-2031">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2032">
                          CAC Certificate
                        </label>
                        <button type="button" onClick={() => simulateUpload(setCacUploaded)} className={`w-full py-6 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${cacUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-2033">
                          {cacUploaded ? <CheckCircle2 size={24} data-id="element-2034" /> : <FileText size={24} data-id="element-2035" />}
                          <span className="font-medium text-sm" data-id="element-2036">
                            {cacUploaded ? 'CAC Certificate Uploaded' : 'Upload CAC Certificate'}
                          </span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2037">
                        <div data-id="element-2038">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2039">
                            Signatory BVN
                          </label>
                          <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="11-digit BVN" data-id="element-2040" />
                        </div>
                        <div data-id="element-2041">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2042">
                            Corporate Bank Name
                          </label>
                          <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-2043">
                            <option value="gtbank" data-id="element-2044">GTBank</option>
                            <option value="access" data-id="element-2045">Access Bank</option>
                            <option value="firstbank" data-id="element-2046">First Bank</option>
                            <option value="uba" data-id="element-2047">UBA</option>
                            <option value="zenith" data-id="element-2048">Zenith Bank</option>
                            <option value="stanbic" data-id="element-2049">Stanbic IBTC</option>
                            <option value="fcmb" data-id="element-2050">FCMB</option>
                            <option value="fidelity" data-id="element-2051">Fidelity Bank</option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-2052">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2053">
                          Corporate Account Number
                        </label>
                        <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="10-digit Account Number" data-id="element-2054" />
                      </div>

                      <div data-id="element-2055">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2056">
                          Proof of Bank Ownership (Bank Statement or Letter)
                        </label>
                        <button type="button" onClick={() => simulateUpload(setBankProofUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${bankProofUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-2057">
                          {bankProofUploaded ? <CheckCircle2 size={24} data-id="element-2058" /> : <CreditCard size={24} data-id="element-2059" />}
                          <span className="font-medium text-sm" data-id="element-2060">
                            {bankProofUploaded ? 'Bank Ownership Proof Uploaded' : 'Upload Bank Statement or Reference Letter'}
                          </span>
                        </button>
                      </div>

                      <div data-id="element-2061">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2062">
                          Office Address
                        </label>
                        <textarea value={officeAddress} onChange={e => setOfficeAddress(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none resize-none" placeholder="Enter full office address" data-id="element-2063" />
                      </div>
                    </>}
                </div>}

              {/* STEP 4: Review */}
              {step === 4 && <div className="space-y-6" data-id="element-2064">
                  <div className="text-center mb-6" data-id="element-2065">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-2066">
                      Review & Submit
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-2067">
                      Please verify your information
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 space-y-4" data-id="element-2068">
                    <div className="flex items-center justify-between pb-4 border-b border-gray-200" data-id="element-2069">
                      <div className="flex items-center gap-3" data-id="element-2070">
                        <div className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center" data-id="element-2071">
                          {accountType === 'individual' ? <User size={20} data-id="element-2072" /> : <Building2 size={20} data-id="element-2073" />}
                        </div>
                        <div data-id="element-2074">
                          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-2075">
                            Account Type
                          </p>
                          <p className="font-bold text-brand-text-dark capitalize" data-id="element-2076">
                            {accountType}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-2" data-id="element-2077">
                      {accountType === 'organization' && <>
                          <div data-id="element-2078">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-2079">
                              Organization Name
                            </p>
                            <p className="text-sm font-medium text-brand-text-dark" data-id="element-2080">
                              {organizationName}
                            </p>
                          </div>
                          <div data-id="element-2081">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-2082">
                              CAC Number
                            </p>
                            <p className="text-sm font-medium text-brand-text-dark" data-id="element-2083">
                              {cacNumber}
                            </p>
                          </div>
                        </>}
                      <div data-id="element-2084">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2085">Full Name</p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2086">
                          {name}
                        </p>
                      </div>
                      <div data-id="element-2087">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2088">Email</p>
                        <p className="text-sm font-medium text-brand-text-dark truncate" data-id="element-2089">
                          {email}
                        </p>
                      </div>
                      <div data-id="element-2090">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2091">Phone</p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2092">
                          {phone}
                        </p>
                      </div>
                      <div data-id="element-2093">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2094">
                          Date of Birth
                        </p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2095">
                          {dateOfBirth}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-2096">
                      <p className="text-sm font-bold text-brand-text-dark mb-3" data-id="element-2097">
                        KYC Documents
                      </p>
                      <div className="space-y-2" data-id="element-2098">
                        {accountType === 'individual' ? <>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2099">
                              <CheckCircle2 size={16} data-id="element-2100" /> Unexpired Photo ID (
                              {idType.toUpperCase()})
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2101">
                              <CheckCircle2 size={16} data-id="element-2102" /> BVN Verified
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2103">
                              <CheckCircle2 size={16} data-id="element-2104" /> Proof of Bank Ownership
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2105">
                              <CheckCircle2 size={16} data-id="element-2106" /> Proof of Address
                            </div>
                          </> : <>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2107">
                              <CheckCircle2 size={16} data-id="element-2108" /> CAC Certificate
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2109">
                              <CheckCircle2 size={16} data-id="element-2110" /> Signatory BVN
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2111">
                              <CheckCircle2 size={16} data-id="element-2112" /> Proof of Bank Ownership
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2113">
                              <CheckCircle2 size={16} data-id="element-2114" /> Office Address
                            </div>
                          </>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-2115">
                    <input type="checkbox" id="terms" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded" data-id="element-2116" />
                    <label htmlFor="terms" className="text-sm text-gray-600 leading-relaxed" data-id="element-2117">
                      I agree to Billvast's{' '}
                      <a href="#" className="text-brand-primary hover:underline" data-id="element-2118">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-brand-primary hover:underline" data-id="element-2119">
                        Privacy Policy
                      </a>
                      . I confirm that all information provided is accurate and
                      I am at least 18 years old.
                    </label>
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-2120">
            {step > 1 ? <button type="button" onClick={handleBack} disabled={isLoading} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50" data-id="element-2121">
                <ArrowLeft size={18} data-id="element-2122" /> Back
              </button> : <div data-id="element-2123"></div>}

            {step < 4 ? <button type="button" onClick={handleNext} className="flex items-center gap-2 px-6 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl font-medium transition-colors shadow-sm" data-id="element-2124">
                Continue <ArrowRight size={18} data-id="element-2125" />
              </button> : <button type="button" onClick={handleSubmit} disabled={isLoading} className="flex items-center justify-center gap-2 px-8 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl font-medium transition-colors shadow-sm disabled:opacity-70" data-id="element-2126">
                {isLoading ? <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2127" />
                    Creating Account...
                  </> : 'Create Account'}
              </button>}
          </div>
        </div>

        <div className="mt-6 text-center" data-id="element-2128">
          <p className="text-sm text-gray-600" data-id="element-2129">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-brand-primary hover:text-brand-primary-dark" data-id="element-2130">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>;
};